import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { Configuration } from '@desygner/ui-core-configuration';
import { DEFAULT_CONFIGURATION as DEFAULT_MONITORING_CONFIGURATION } from '@desygner/ui-core-monitoring';

import { getTranslatedLocaleUrls, readUserLanguage } from '../utils/locale.utils';

type GenericAnimationImages = Record<string, string> & {
  aiToolsBanners: Record<string, string>;
  animationImages: Record<string, string>;
  placeholders: Record<string, string>;
  coachmarks: Record<string, string>;
  restrictionsOverviewPlaceholder: string;
};

export type GenericFeature = {
  [key: string]: boolean | string;
};

export type Features = GenericFeature & {
  library:
    | false
    | {
        addToLibrary: boolean;
        user: boolean;
        workspace: boolean;
      };
  stock:
    | false
    | {
        text: boolean;
        image: boolean;
        vector: boolean;
      };
  workspace: false | Record<string, Record<string, boolean | number | string | null | unknown>>;
} & {
  defaultTheme?: 'light' | 'dark';
  openEditorInNewTab?: boolean;
  collaboration:
    | false
    | {
        maxCollaborators: number;
        resendInterval: number;
      };
  paywall: {
    aiPanels: boolean;
    fonts: boolean;
    projectHistory: boolean;
    download: {
      pngTransparentBackground: boolean;
      pngQuality: boolean;
      jpegQuality: boolean;
      pdfPrintOutput: boolean;
      pdfStandardOutput: boolean;
      pdfPrintCMYK: boolean;
      videoOutput: boolean;
      videoMaxQuality: boolean;
    };
    qrCode: boolean;
    template: boolean;
  };
  restrictGuestUser: boolean;
};

export interface IConfigImages {
  default: GenericAnimationImages;
  light: GenericAnimationImages;
  dark: GenericAnimationImages;
  workspace?: { light: GenericAnimationImages; dark: GenericAnimationImages };
}

type ExitEditorConfig = {
  design: string;
  template: string;
  homePage: string;
};

export interface ConfigStore extends Configuration {
  enterpriseRegisterURL: string;
  exitEditor: ExitEditorConfig;
  feature: Features;
  fontPathPrefix?: string;
  googleClientId: string;
  googleTagManager: { id: string };
  hubSpot: {
    portalId: string;
    formId: string;
  };
  images: IConfigImages;
  language: string;
  print: {
    linkToPrintableTemplates: string;
  };
  release: string;
  translationUrls: string[];
}

export interface IConfigStore extends ConfigStore {
  updateFeature: (key: string, value: boolean) => void;
}

const config: Partial<IConfigStore> = (process.env.CONFIG as Partial<IConfigStore>) || {};

const language = readUserLanguage();

const exitEditorConfig = (config.exitEditor ?? {}) as Partial<ExitEditorConfig>;

const exitEditorInitial: ExitEditorConfig = {
  design: exitEditorConfig?.design ?? '',
  template: exitEditorConfig?.template ?? '',
  homePage: exitEditorConfig?.homePage ?? '/',
};

const initialState: ConfigStore = {
  auth: config.auth ?? 'Bearer',
  api: config.api ?? {
    aiServerUrl: '',
    apiUrl: '',
    authUrl: '',
    baseUrl: '',
    fluerApiUrl: '',
    webrandUrl: '',
    editorWebrandUrl: '',
  },
  images: config.images!,
  brand: config.brand ?? {
    name: 'Fluer',
    brandColor: '',
    companyId: 0,
    faviconUrl: '',
    logoUrl: '',
  },
  environment: config.environment ?? 'develop',
  enterpriseRegisterURL: config.enterpriseRegisterURL ?? '',
  exitEditor: exitEditorInitial,
  feature: config.feature || ({} as Features),
  fontPathPrefix: config.fontPathPrefix ?? '',
  googleClientId: config.googleClientId ?? '',
  googleTagManager: {
    id: config.googleTagManager?.id ?? '',
  },
  hubSpot: {
    portalId: config.hubSpot?.portalId ?? '',
    formId: config.hubSpot?.formId ?? '',
  },
  language: language,
  logLevel: config.logLevel ?? 'error',
  print: config.print ?? {
    linkToPrintableTemplates: '',
  },
  release: config.release ?? config.environment ?? 'develop',
  resources: config.resources ?? {
    assetsUrl: '',
    cookieDomain: '',
    domainUrl: '',
    desygnerBucketUrl: '',
    socketUrl: '',
    staticUrl: '',
    viewerUrl: '',
    deepLinkUrl: '',
  },
  sentry: {
    appName: config.sentry?.appName ?? '',
    dsn: config.sentry?.dsn ?? '',
    levels: config.sentry?.levels ?? DEFAULT_MONITORING_CONFIGURATION.levels,
    breadcrumbs: config.sentry?.breadcrumbs ?? DEFAULT_MONITORING_CONFIGURATION.breadcrumbs,
    maxBreadcrumbs:
      config.sentry?.maxBreadcrumbs ?? DEFAULT_MONITORING_CONFIGURATION.maxBreadcrumbs,
    replay: config.sentry?.replay ?? DEFAULT_MONITORING_CONFIGURATION.replay,
  },
  stripe: config.stripe ?? {
    print: '',
    desygner: '',
    'sing-shutter': '',
    'sing-sub': '',
    'sing-smb': '',
    'sing-pro': '',
  },
  translationUrls: getTranslatedLocaleUrls(config.translationUrls ?? [], language),
  upload: config.upload ?? {
    companionUrl: '',
    objectKey: '',
    bucketName: '',
  },
  user: config.user ?? { proUserRole: [], workspaceProBypassEnabled: false },
  ai: {
    useWorkspaceCredits: false,
  },
};

export const useConfigStore = create<IConfigStore>()(
  devtools(
    immer((set) => ({
      ...initialState,
      updateFeature: (key: string, value: boolean) =>
        set((state) => {
          const keys = key.split('.');
          let feature: any = state.feature;

          for (let i = 0; i < keys.length - 1; i++) {
            feature = feature[keys[i]];
          }

          feature[keys[keys.length - 1]] = value;
        }),
    })),
    { name: 'Viewer | Config' }
  )
);
